<template>
    <Teleport to="#modals">
        <Transition>
            <div
                v-show="visible"
                ref="scroller"
                class="fixed inset-0 z-50 bg-brand-2-950 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
            >
                <!-- Zero-height sticky anchor so the chrome floats over the images
                     (Airbnb pattern) instead of consuming layout height — the first
                     image stays full-bleed from the top. -->
                <div class="sticky top-0 z-10 h-0 overflow-visible">
                    <div class="flex items-start justify-between p-3 pointer-events-none">
                        <p
                            v-if="total"
                            class="inline-flex items-center gap-2.5 py-2 px-2.5 rounded-sm bg-white text-brand-2-950 font-semibold text-xssss tabular-nums pointer-events-auto"
                        >
                            <span
                                class="size-[18px]"
                                v-html="CameraIcon"
                            ></span>
                            {{ currentSlide }} / {{ total }}
                        </p>

                        <ClearButton
                            class="ml-auto bg-brand-2-950/60 hover:bg-brand-2-950/80 backdrop-blur-sm border-transparent text-white pointer-events-auto"
                            @click.prevent="emits('close')"
                        />
                    </div>
                </div>

                <!-- Server-rendered, Optimole-owned image list is projected here. -->
                <div
                    ref="list"
                    class="pb-small space-y-3"
                >
                    <slot />
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { ref, watch, nextTick, onBeforeUnmount } from 'vue';

    import ClearButton from '@js/src/components/ClearButton.vue';

    import { useBackToClose } from '@js/src/composables/useBackToClose';

    import CameraIcon from '@img/icons/camera.svg?raw';

    const props = defineProps([
        'active',
        'total',
        'slide',
    ]);

    const emits = defineEmits(['close']);

    // Below the 501px `sm` breakpoint == the vertical-scroll variant. The variant
    // is fixed at open time (not live-swapped on resize) so it stays in lockstep
    // with ImageGalleryModal, which gates its desktop carousel on the inverse.
    const MOBILE_QUERY = '(max-width: 500px)';

    const visible = ref(false);
    const currentSlide = ref(1);
    const scroller = ref(null);
    const list = ref(null);

    let observer = null;

    // Back button dismisses the overlay instead of navigating off the house page.
    const backToClose = useBackToClose(() => emits('close'));

    watch(() => props.active, (active) => {
        if (!active) {
            // Consumes the pushed history entry on UI-driven (X / Escape) closes;
            // no-op when a back press already closed us or we never opened.
            backToClose.close();
            visible.value = false;

            return;
        }

        // Only take over on mobile; on desktop ImageGalleryModal owns the carousel.
        if (!window.matchMedia(MOBILE_QUERY).matches) {
            return;
        }

        visible.value = true;
        backToClose.open();
        currentSlide.value = props.slide + 1;

        nextTick(() => {
            setupObserver();
            scrollToSlide(props.slide);
        });
    });

    function setupObserver() {
        if (!list.value || !scroller.value) {
            return;
        }

        if (observer) {
            observer.disconnect();
        }

        const items = Array.from(list.value.children);

        // Anchor the counter to the TOP of the viewport: the figure occupying the
        // top edge is "current". The detection band is the top 10% of the root
        // (bottom margin -90%). These images are wide/short, so a centre-of-viewport
        // band would read "2" at the very top — top-anchoring fixes that.
        const intersecting = new Set();

        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const index = items.indexOf(entry.target);

                if (index < 0) {
                    return;
                }

                if (entry.isIntersecting) {
                    intersecting.add(index);
                } else {
                    intersecting.delete(index);
                }
            });

            // Topmost intersecting figure wins. If nothing intersects mid-transition,
            // hold the last value so the counter never flickers.
            if (intersecting.size > 0) {
                currentSlide.value = Math.min(...intersecting) + 1;
            }
        }, {
            root: scroller.value,
            rootMargin: '0px 0px -90% 0px',
            threshold: 0,
        });

        items.forEach((item) => observer.observe(item));
    }

    function scrollToSlide(index) {
        if (!list.value) {
            return;
        }

        const item = list.value.children[index];

        if (item) {
            item.scrollIntoView({ block: 'start' });
        }
    }

    onBeforeUnmount(() => {
        if (observer) {
            observer.disconnect();
        }
    });
</script>
